import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type KeyboardEvent } from 'react';
import {
  Button,
  ConfirmDialog,
  ErrorNote,
  Loading,
  Modal,
  Toggle,
  inputClass,
} from '../../components/ui';
import { errorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth-context-value';
import { formatShortDate, initials } from '../../lib/format';
import { PERMISSION_GROUPS, adminPermissions, permissionLabel } from '../../lib/permissions';
import {
  connectionsApi,
  departmentsApi,
  invitations as invitationsApi,
  memberMultiserviceApi,
  members as membersApi,
  pipelinesApi,
} from '../../lib/resources';
import { FLAG_LABELS } from '../../lib/stage-rule-fields';
import type {
  CreatedInvitation,
  Member,
  MemberMultiservice,
  MemberPipelineRow,
  PermissionCode,
} from '../../types';

/** Configurações › Membros — mestre-detalhe, como no DataCrazy. */
export function MembersSection() {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);

  const query = useQuery({
    queryKey: ['members', search],
    queryFn: () => membersApi.list(search || undefined),
  });
  const members = query.data?.data ?? [];
  const selected = members.find((member) => member.id === selectedId) ?? members[0];

  return (
    <div className="space-y-5">
      <div className="flex gap-5">
        <aside className="w-80 shrink-0 rounded-xl border border-slate-200 bg-white">
          <div className="space-y-2 border-b border-slate-100 p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-900">Membros</h2>
              <Button variant="primary" onClick={() => setInviting(true)}>
                Convidar membro
              </Button>
            </div>
            <input
              className={`${inputClass} mt-0`}
              placeholder="Buscar por nome ou e-mail"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <p className="text-xs text-slate-400">{query.data?.count ?? 0} membro(s)</p>
          </div>
          {query.isLoading ? (
            <Loading />
          ) : (
            <ul className="max-h-[60vh] divide-y divide-slate-100 overflow-y-auto">
              {members.map((member) => (
                <li key={member.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(member.id)}
                    className={`flex w-full items-center gap-3 px-4 py-3 text-left ${
                      selected?.id === member.id ? 'bg-slate-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <Avatar name={member.name} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-800">
                        {member.name}
                      </span>
                      <span className="block truncate text-xs text-slate-500">
                        {member.email}
                      </span>
                    </span>
                    <span className="text-right text-[11px] text-slate-400">
                      {member.isAdmin ? (
                        <span className="rounded-full bg-violet-50 px-2 py-0.5 font-medium text-violet-700">
                          Administrador
                        </span>
                      ) : (
                        `${member.permissions.length} papéis`
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <div className="min-w-0 flex-1">
          {selected ? (
            <MemberDetail key={selected.id} member={selected} />
          ) : (
            !query.isLoading && (
              <p className="text-sm text-slate-400">Nenhum membro encontrado.</p>
            )
          )}
        </div>
      </div>

      <PendingInvitations />
      {inviting && <InviteModal onClose={() => setInviting(false)} />}
    </div>
  );
}

function Avatar({ name }: { name: string }) {
  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-600">
      {initials(name)}
    </span>
  );
}

function MemberDetail({ member }: { member: Member }) {
  const queryClient = useQueryClient();
  const { session, refreshSession } = useAuth();
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState<'logout' | 'remove' | null>(null);
  const isSelf = session?.access.membershipId === member.id;
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['members'] });

  const forceLogout = useMutation({
    mutationFn: () => membersApi.forceLogout(member.id),
    onSuccess: () => {
      setConfirm(null);
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: () => membersApi.remove(member.id),
    onSuccess: () => {
      setConfirm(null);
      invalidate();
    },
  });

  return (
    <section className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <Avatar name={member.name} />
            <div>
              <p className="text-base font-semibold text-slate-900">
                {member.name}{' '}
                {isSelf && <span className="text-xs font-normal text-slate-400">(você)</span>}
              </p>
              <p className="text-xs text-slate-500">
                {member.email} · <span className="text-emerald-600">Ativo</span> · desde{' '}
                {formatShortDate(member.createdAt)}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setEditing(true)}>Editar papéis</Button>
            {!member.isOwner && !isSelf && (
              <>
                <Button variant="ghost" onClick={() => setConfirm('logout')}>
                  Forçar logout
                </Button>
                <Button variant="danger" onClick={() => setConfirm('remove')}>
                  Remover
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {member.isOwner && <Chip strong>Dono do workspace</Chip>}
          {member.isAdmin && <Chip strong>Administrador</Chip>}
          {member.permissions.map((code) => (
            <Chip key={code}>{permissionLabel(code)}</Chip>
          ))}
          {!member.isAdmin && member.permissions.length === 0 && (
            <span className="text-xs text-slate-400">
              Sem papéis: vê só os cadastros, sem leads nem pipelines.
            </span>
          )}
        </div>
        {member.sessionsRevokedAt && (
          <p className="mt-3 text-xs text-slate-400">
            Sessões encerradas por um administrador em{' '}
            {new Date(member.sessionsRevokedAt).toLocaleString('pt-BR')}.
          </p>
        )}
      </div>

      <MemberPipelines member={member} />

      <MemberMultiserviceBlock member={member} />

      {editing && (
        <PermissionsModal
          title={`Editar papéis — ${member.name}`}
          initialAdmin={member.isAdmin}
          initialPermissions={member.permissions}
          lockAdmin={member.isOwner || isSelf}
          submitLabel="Salvar papéis"
          onClose={() => setEditing(false)}
          onSubmit={async (isAdmin, permissions) => {
            await membersApi.updatePermissions(member.id, isAdmin, permissions);
            invalidate();
            if (isSelf) await refreshSession();
            setEditing(false);
          }}
        />
      )}

      {confirm === 'logout' && (
        <ConfirmDialog
          title="Forçar logout?"
          message={`Todas as sessões abertas de ${member.name} neste workspace são encerradas agora. Ele(a) precisa entrar de novo.`}
          confirmLabel="Forçar logout"
          busy={forceLogout.isPending}
          error={forceLogout.error ? errorMessage(forceLogout.error) : null}
          onConfirm={() => forceLogout.mutate()}
          onClose={() => setConfirm(null)}
        />
      )}
      {confirm === 'remove' && (
        <ConfirmDialog
          title="Remover membro?"
          message={`${member.name} perde o acesso a este workspace. Os leads e negócios em que é responsável continuam como estão.`}
          confirmLabel="Remover"
          danger
          busy={remove.isPending}
          error={remove.error ? errorMessage(remove.error) : null}
          onConfirm={() => remove.mutate()}
          onClose={() => setConfirm(null)}
        />
      )}
    </section>
  );
}

/**
 * Bloco "CRM e pipelines": em quais pipelines a pessoa entra e com que travas.
 * Administrador e "Administrador de pipelines" veem todas — o bloco só explica.
 */
function MemberPipelines({ member }: { member: Member }) {
  const queryClient = useQueryClient();
  const fullAccess = member.isAdmin || member.permissions.includes('pipeline_admin');
  const query = useQuery({
    queryKey: ['member-pipelines', member.id],
    queryFn: () => pipelinesApi.ofMember(member.id),
    enabled: !fullAccess,
  });
  const [draft, setDraft] = useState<MemberPipelineRow[] | null>(null);
  const rows = draft ?? query.data ?? [];
  const save = useMutation({
    mutationFn: () =>
      pipelinesApi.setOfMember(
        member.id,
        rows
          .filter((row) => row.isMember)
          .map((row) => ({
            pipelineId: row.pipelineId,
            showOnlyMine: row.showOnlyMine,
            disableCreate: row.disableCreate,
            disableDelete: row.disableDelete,
            disableAttendantChange: row.disableAttendantChange,
          })),
      ),
    onSuccess: (result) => {
      queryClient.setQueryData(['member-pipelines', member.id], result);
      queryClient.invalidateQueries({ queryKey: ['pipeline-members'] });
      setDraft(null);
    },
  });
  const update = (pipelineId: string, patch: Partial<MemberPipelineRow>) =>
    setDraft(rows.map((row) => (row.pipelineId === pipelineId ? { ...row, ...patch } : row)));

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <p className="text-sm font-semibold text-slate-800">CRM e pipelines</p>
      {fullAccess ? (
        <p className="mt-1 text-xs text-slate-500">
          {member.isAdmin ? 'Administrador' : 'Administrador de pipelines'}: vê e configura
          todas as pipelines, sem travas.
        </p>
      ) : !member.permissions.some((code) => code === 'pipeline_member') ? (
        <p className="mt-1 text-xs text-slate-500">
          Sem o papel "Membro de pipelines", a pessoa não abre pipelines nem negócios. Dê o
          papel em Editar papéis.
        </p>
      ) : query.isLoading ? (
        <Loading />
      ) : (
        <div className="mt-3 space-y-3">
          <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {rows.map((row) => (
              <div key={row.pipelineId} className="space-y-2 px-4 py-2.5">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={row.isMember}
                    onChange={(event) =>
                      update(row.pipelineId, { isMember: event.target.checked })
                    }
                  />
                  <span className="font-medium text-slate-800">{row.name}</span>
                  {row.group && <span className="text-xs text-slate-400">{row.group}</span>}
                </label>
                {row.isMember && (
                  <div className="grid gap-2 pl-6 sm:grid-cols-2">
                    {FLAG_LABELS.map(([flag, label]) => (
                      <Toggle
                        key={flag}
                        checked={row[flag]}
                        onChange={(checked) => update(row.pipelineId, { [flag]: checked })}
                        label={label}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
            {rows.length === 0 && (
              <p className="px-4 py-4 text-xs text-slate-400">Nenhuma pipeline no workspace.</p>
            )}
          </div>
          {save.error && <ErrorNote>{errorMessage(save.error)}</ErrorNote>}
          <div className="flex items-center justify-end gap-3">
            {draft && (
              <span className="text-xs font-medium text-amber-600">Alterações não salvas</span>
            )}
            <Button
              variant="primary"
              disabled={!draft || save.isPending}
              onClick={() => save.mutate()}
            >
              Salvar pipelines
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Bloco "Multiatendimento e departamentos" (doc 01 §2): os 3 interruptores,
 * as conexões que a pessoa acessa e os departamentos em que atende.
 */
function MemberMultiserviceBlock({ member }: { member: Member }) {
  const queryClient = useQueryClient();
  const canAttend =
    member.isAdmin || member.permissions.some((code) => code.startsWith('multiservice_'));
  const query = useQuery({
    queryKey: ['member-multiservice', member.id],
    queryFn: () => memberMultiserviceApi.get(member.id),
    enabled: canAttend,
  });
  const departments = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsApi.list(),
    enabled: canAttend,
  });
  const connections = useQuery({
    queryKey: ['connections'],
    queryFn: connectionsApi.list,
    enabled: canAttend,
  });
  const [draft, setDraft] = useState<MemberMultiservice | null>(null);
  const value = draft ?? query.data;
  const save = useMutation({
    mutationFn: () => memberMultiserviceApi.set(member.id, value!),
    onSuccess: (result) => {
      queryClient.setQueryData(['member-multiservice', member.id], result);
      setDraft(null);
    },
  });
  const toggleId = (key: 'departmentIds' | 'connectionIds', id: string) => {
    if (!value) return;
    const list = value[key];
    setDraft({
      ...value,
      [key]: list.includes(id) ? list.filter((item) => item !== id) : [...list, id],
    });
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <p className="text-sm font-semibold text-slate-800">Multiatendimento e departamentos</p>
      {!canAttend ? (
        <p className="mt-1 text-xs text-slate-500">
          Sem papel de multiatendimento, a pessoa não usa o chat. Dê o papel em Editar papéis.
        </p>
      ) : !value ? (
        <Loading />
      ) : (
        <div className="mt-3 space-y-4">
          <div className="grid gap-2 sm:grid-cols-3">
            <Toggle
              checked={value.settings.seeOthersConversations}
              onChange={(checked) =>
                setDraft({
                  ...value,
                  settings: { ...value.settings, seeOthersConversations: checked },
                })
              }
              label="Ver conversas de outros atendentes"
            />
            <Toggle
              checked={value.settings.hideUnassigned}
              onChange={(checked) =>
                setDraft({ ...value, settings: { ...value.settings, hideUnassigned: checked } })
              }
              label="Esconder conversas sem atendente"
            />
            <Toggle
              checked={value.settings.restrictConnections}
              onChange={(checked) =>
                setDraft({
                  ...value,
                  settings: { ...value.settings, restrictConnections: checked },
                })
              }
              label="Só as conexões marcadas"
            />
          </div>
          {value.settings.restrictConnections && (
            <div>
              <p className="mb-1 text-xs font-medium text-slate-600">Conexões</p>
              <div className="flex flex-wrap gap-1.5">
                {(connections.data?.data ?? []).map((connection) => (
                  <button
                    key={connection.id}
                    type="button"
                    onClick={() => toggleId('connectionIds', connection.id)}
                    className={`rounded-full border px-2.5 py-1 text-xs ${
                      value.connectionIds.includes(connection.id)
                        ? 'border-slate-900 bg-slate-900 text-white'
                        : 'border-slate-300 text-slate-600'
                    }`}
                  >
                    {connection.name}
                  </button>
                ))}
                {(connections.data?.data ?? []).length === 0 && (
                  <span className="text-xs text-slate-400">Nenhuma conexão cadastrada.</span>
                )}
              </div>
            </div>
          )}
          <div>
            <p className="mb-1 text-xs font-medium text-slate-600">
              Departamentos em que atende
            </p>
            <div className="flex flex-wrap gap-1.5">
              {(departments.data?.data ?? []).map((department) => (
                <button
                  key={department.id}
                  type="button"
                  onClick={() => toggleId('departmentIds', department.id)}
                  className={`rounded-full border px-2.5 py-1 text-xs ${
                    value.departmentIds.includes(department.id)
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-300 text-slate-600'
                  }`}
                >
                  {department.name}
                </button>
              ))}
              {(departments.data?.data ?? []).length === 0 && (
                <span className="text-xs text-slate-400">Nenhum departamento cadastrado.</span>
              )}
            </div>
          </div>
          {save.error && <ErrorNote>{errorMessage(save.error)}</ErrorNote>}
          <div className="flex items-center justify-end gap-3">
            {draft && (
              <span className="text-xs font-medium text-amber-600">Alterações não salvas</span>
            )}
            <Button
              variant="primary"
              disabled={!draft || save.isPending}
              onClick={() => save.mutate()}
            >
              Salvar multiatendimento
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Chip({ children, strong }: { children: React.ReactNode; strong?: boolean }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs ${
        strong ? 'bg-violet-50 font-medium text-violet-700' : 'bg-slate-100 text-slate-600'
      }`}
    >
      {children}
    </span>
  );
}

/** O bloco "Acesso" do convite e da edição de papéis. Nenhum papel vem marcado. */
function PermissionsPicker({
  isAdmin,
  permissions,
  lockAdmin,
  onChange,
}: {
  isAdmin: boolean;
  permissions: PermissionCode[];
  lockAdmin?: boolean;
  onChange: (isAdmin: boolean, permissions: PermissionCode[]) => void;
}) {
  const toggle = (code: PermissionCode) =>
    onChange(
      isAdmin,
      permissions.includes(code)
        ? permissions.filter((item) => item !== code)
        : [...permissions, code],
    );

  return (
    <div className="space-y-4">
      <Toggle
        label="Administrador"
        hint="Acesso completo: visualização, edição e gestão de membros."
        checked={isAdmin}
        disabled={lockAdmin}
        onChange={(checked) => onChange(checked, permissions)}
      />
      <div className="space-y-3">
        {PERMISSION_GROUPS.map((group) => (
          <fieldset
            key={group.group}
            className={`rounded-lg border border-slate-200 p-3 ${
              isAdmin && !group.alsoForAdmins ? 'pointer-events-none opacity-40' : ''
            }`}
          >
            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
              {group.group}
            </legend>
            <div className="space-y-2">
              {group.items.map((item) => (
                <label key={item.code} className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={permissions.includes(item.code)}
                    onChange={() => toggle(item.code)}
                  />
                  <span>
                    <span className="font-medium text-slate-700">{item.label}</span>
                    <span className="block text-xs text-slate-400">{item.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        ))}
      </div>
    </div>
  );
}

function PermissionsModal({
  title,
  initialAdmin,
  initialPermissions,
  lockAdmin,
  submitLabel,
  onSubmit,
  onClose,
}: {
  title: string;
  initialAdmin: boolean;
  initialPermissions: PermissionCode[];
  lockAdmin?: boolean;
  submitLabel: string;
  onSubmit: (isAdmin: boolean, permissions: PermissionCode[]) => Promise<void>;
  onClose: () => void;
}) {
  const [isAdmin, setIsAdmin] = useState(initialAdmin);
  const [permissions, setPermissions] = useState(initialPermissions);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  return (
    <Modal title={title} onClose={onClose} wide>
      <PermissionsPicker
        isAdmin={isAdmin}
        permissions={permissions}
        lockAdmin={lockAdmin}
        onChange={(admin, codes) => {
          setIsAdmin(admin);
          setPermissions(codes);
        }}
      />
      {lockAdmin && (
        <p className="mt-3 text-xs text-slate-400">
          O dono do workspace e você mesmo não perdem o acesso de administrador por aqui.
        </p>
      )}
      {error && (
        <div className="mt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="primary"
          disabled={saving}
          onClick={async () => {
            setSaving(true);
            setError(null);
            try {
              await onSubmit(isAdmin, isAdmin ? adminPermissions(permissions) : permissions);
            } catch (submitError) {
              setError(errorMessage(submitError, 'Não foi possível salvar'));
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? 'Salvando…' : submitLabel}
        </Button>
      </div>
    </Modal>
  );
}

const inviteLink = (invitation: CreatedInvitation) =>
  `${window.location.origin}${invitation.acceptPath}`;

/** "Convidar membro": e-mails em chips (espaço, vírgula ou Enter), papéis e os links para copiar. */
function InviteModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [permissions, setPermissions] = useState<PermissionCode[]>([]);
  const [result, setResult] = useState<Awaited<
    ReturnType<typeof invitationsApi.create>
  > | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const commitDraft = () => {
    const parts = draft
      .split(/[\s,;]+/)
      .map((part) => part.trim().toLowerCase())
      .filter(Boolean);
    if (parts.length > 0) setEmails((current) => [...new Set([...current, ...parts])]);
    setDraft('');
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if ([' ', 'Enter', ',', ';'].includes(event.key)) {
      event.preventDefault();
      commitDraft();
    } else if (event.key === 'Backspace' && draft === '') {
      setEmails((current) => current.slice(0, -1));
    }
  };

  const send = useMutation({
    mutationFn: () => {
      const pending = draft.trim() ? [...emails, draft.trim().toLowerCase()] : emails;
      return invitationsApi.create(
        [...new Set(pending)],
        isAdmin,
        isAdmin ? adminPermissions(permissions) : permissions,
      );
    },
    onSuccess: (data) => {
      setResult(data);
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });

  const copy = async (invitation: CreatedInvitation) => {
    await navigator.clipboard.writeText(inviteLink(invitation));
    setCopied(invitation.id);
  };

  if (result) {
    return (
      <Modal title="Convites criados" onClose={onClose} wide>
        <div className="space-y-4 text-sm">
          <p className="text-slate-600">
            Não há envio de e-mail: copie cada link e mande para a pessoa. Os links valem por 7
            dias e só aparecem agora.
          </p>
          <ul className="space-y-2">
            {result.created.map((invitation) => (
              <li
                key={invitation.id}
                className="flex items-center gap-3 rounded-lg border border-slate-200 p-3"
              >
                <span className="min-w-0 flex-1">
                  <span className="block font-medium text-slate-800">{invitation.email}</span>
                  <span className="block truncate text-xs text-slate-400">
                    {inviteLink(invitation)}
                  </span>
                </span>
                <Button onClick={() => void copy(invitation)}>
                  {copied === invitation.id ? 'Copiado!' : 'Copiar link'}
                </Button>
              </li>
            ))}
          </ul>
          {result.skipped.length > 0 && (
            <div className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
              {result.skipped.map((item) => (
                <p key={item.email}>
                  {item.email}: {item.reason}
                </p>
              ))}
            </div>
          )}
          <div className="flex justify-end">
            <Button variant="primary" onClick={onClose}>
              Concluir
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Convidar membro" onClose={onClose} wide>
      <div className="space-y-4">
        <div className="text-sm">
          <span className="font-medium text-slate-700">Quem</span>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 rounded-lg border border-slate-300 px-2 py-1.5 focus-within:border-slate-900">
            {emails.map((email) => (
              <span
                key={email}
                className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
              >
                {email}
                <button
                  type="button"
                  aria-label={`Tirar ${email}`}
                  onClick={() =>
                    setEmails((current) => current.filter((item) => item !== email))
                  }
                  className="text-slate-400 hover:text-slate-700"
                >
                  ×
                </button>
              </span>
            ))}
            <input
              autoFocus
              className="min-w-[12rem] flex-1 py-1 text-sm outline-none"
              placeholder={
                emails.length === 0 ? 'Digite um e-mail e pressione espaço ou enter' : ''
              }
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={onKeyDown}
              onBlur={commitDraft}
            />
          </div>
        </div>

        <div>
          <p className="mb-2 text-sm font-medium text-slate-700">Acesso</p>
          <PermissionsPicker
            isAdmin={isAdmin}
            permissions={permissions}
            onChange={(admin, codes) => {
              setIsAdmin(admin);
              setPermissions(codes);
            }}
          />
        </div>

        {send.error && (
          <ErrorNote>{errorMessage(send.error, 'Não foi possível convidar')}</ErrorNote>
        )}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={send.isPending || (emails.length === 0 && draft.trim() === '')}
            onClick={() => send.mutate()}
          >
            {send.isPending ? 'Criando…' : 'Criar convite'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Convites que ainda não foram aceitos — dá para revogar ou gerar um link novo. */
function PendingInvitations() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['invitations'], queryFn: invitationsApi.list });
  const [revoking, setRevoking] = useState<string | null>(null);
  const [renewed, setRenewed] = useState<CreatedInvitation | null>(null);
  const [copied, setCopied] = useState(false);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['invitations'] });

  const revoke = useMutation({
    mutationFn: (id: string) => invitationsApi.revoke(id),
    onSuccess: () => {
      setRevoking(null);
      invalidate();
    },
  });
  const renew = useMutation({
    mutationFn: (invitation: {
      email: string;
      isAdmin: boolean;
      permissions: PermissionCode[];
    }) => invitationsApi.create([invitation.email], invitation.isAdmin, invitation.permissions),
    onSuccess: (data) => {
      setCopied(false);
      setRenewed(data.created[0] ?? null);
      invalidate();
    },
  });

  const pending = query.data ?? [];
  if (pending.length === 0) return null;

  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <header className="border-b border-slate-100 px-5 py-3">
        <h2 className="text-sm font-semibold text-slate-900">Convites pendentes</h2>
      </header>
      <ul className="divide-y divide-slate-100">
        {pending.map((invitation) => (
          <li key={invitation.id} className="flex items-center gap-3 px-5 py-3 text-sm">
            <span className="min-w-0 flex-1">
              <span className="block font-medium text-slate-800">{invitation.email}</span>
              <span className="block text-xs text-slate-400">
                {invitation.isAdmin
                  ? 'Administrador'
                  : `${invitation.permissions.length} papéis`}{' '}
                ·{' '}
                {invitation.expired ? (
                  <span className="text-red-600">vencido</span>
                ) : (
                  `vale até ${formatShortDate(invitation.expiresAt)}`
                )}
              </span>
            </span>
            <Button onClick={() => renew.mutate(invitation)} disabled={renew.isPending}>
              Gerar novo link
            </Button>
            <Button variant="ghost" onClick={() => setRevoking(invitation.id)}>
              Revogar
            </Button>
          </li>
        ))}
      </ul>

      {revoking && (
        <ConfirmDialog
          title="Revogar convite?"
          message="O link deixa de funcionar."
          confirmLabel="Revogar"
          danger
          busy={revoke.isPending}
          onConfirm={() => revoke.mutate(revoking)}
          onClose={() => setRevoking(null)}
        />
      )}
      {renewed && (
        <Modal title="Novo link de convite" onClose={() => setRenewed(null)}>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              O link antigo de <strong>{renewed.email}</strong> não vale mais. Copie o novo:
            </p>
            <p className="break-all rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
              {inviteLink(renewed)}
            </p>
            <div className="flex justify-end">
              <Button
                variant="primary"
                onClick={async () => {
                  await navigator.clipboard.writeText(inviteLink(renewed));
                  setCopied(true);
                }}
              >
                {copied ? 'Copiado!' : 'Copiar link'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
}
